import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { CartItem, createStorefrontCheckout } from '@/lib/shopify';

const NITRILE_MIN_QUANTITY = 10;
const NITRILE_PRICE_PER_UNIT = 9;

function isNitrileGlovesItem(item: CartItem | undefined | null) {
  const handle = item?.product?.node?.handle ?? '';
  return handle.includes('כפפות-ניטריל') || handle.includes('nitrile-gloves');
}

function normalizeCartItem(item: CartItem): CartItem {
  // 1) Quantity must always be a sane integer
  const rawQty = Number(item.quantity);
  const safeQty = Number.isFinite(rawQty) ? Math.floor(rawQty) : 1;
  const quantity = Math.max(1, safeQty);

  // 2) For most products, price must always reflect the selected variant (prevents stale/persisted prices)
  const variantFromProduct =
    item.product?.node?.variants?.edges?.find((e) => e.node.id === item.variantId)?.node ?? null;

  const base: CartItem = {
    ...item,
    quantity,
    price: variantFromProduct?.price ?? item.price,
    selectedOptions: item.selectedOptions ?? (variantFromProduct?.selectedOptions ?? []),
    variantTitle: item.variantTitle ?? variantFromProduct?.title ?? "Default Title",
  };

  // 3) Special-case business rules
  if (!isNitrileGlovesItem(base)) return base;
  return {
    ...base,
    quantity: Math.max(base.quantity, NITRILE_MIN_QUANTITY),
    price: {
      ...base.price,
      amount: String(NITRILE_PRICE_PER_UNIT),
    },
  };
}

function dedupeByVariantId(items: CartItem[]): CartItem[] {
  const map = new Map<string, CartItem>();
  for (const raw of items) {
    const item = normalizeCartItem(raw);
    const existing = map.get(item.variantId);
    if (!existing) {
      map.set(item.variantId, item);
      continue;
    }

    const isNitrile = isNitrileGlovesItem(item) || isNitrileGlovesItem(existing);
    const mergedQty = isNitrile ? item.quantity : existing.quantity + item.quantity;
    map.set(item.variantId, normalizeCartItem({ ...existing, ...item, quantity: mergedQty }));
  }
  return Array.from(map.values());
}

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  
  addItem: (item: CartItem) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  removeItem: (variantId: string) => void;
  clearCart: () => void;
  setCartId: (cartId: string) => void;
  setCheckoutUrl: (url: string) => void;
  setLoading: (loading: boolean) => void;
  createCheckout: (forceNew?: boolean) => Promise<string | null>;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,

      addItem: (item) => {
        const normalized = normalizeCartItem(item);
        const { items } = get();
        const existingItem = items.find(i => i.variantId === normalized.variantId);

        // Any cart change invalidates a previously created checkout URL
        // (otherwise users can be sent to an old checkout with different items).
        if (existingItem) {
          const isNitrile = isNitrileGlovesItem(existingItem) || isNitrileGlovesItem(normalized);
          const nextQuantity = isNitrile
            ? normalized.quantity // deterministic for nitrile gloves: set exact quantity
            : existingItem.quantity + normalized.quantity;

          const merged = normalizeCartItem({
            // keep any existing data, but prefer the latest incoming item fields
            ...existingItem,
            ...normalized,
            quantity: nextQuantity,
          });

          set({
            items: items.map(i =>
              i.variantId === normalized.variantId
                ? merged
                : i
            ),
            checkoutUrl: null,
            cartId: null,
          });
        } else {
          set({ items: [...items, normalized], checkoutUrl: null, cartId: null });
        }
      },

      updateQuantity: (variantId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(variantId);
          return;
        }

        const existing = get().items.find((i) => i.variantId === variantId);
        if (existing && isNitrileGlovesItem(existing) && quantity < NITRILE_MIN_QUANTITY) {
          quantity = NITRILE_MIN_QUANTITY;
        }

        set({
          items: get().items.map(item => {
            if (item.variantId !== variantId) return item;
            return normalizeCartItem({ ...item, quantity });
          }),
          checkoutUrl: null,
          cartId: null,
        });
      },

      removeItem: (variantId) => {
        set({
          items: get().items.filter(item => item.variantId !== variantId),
          checkoutUrl: null,
          cartId: null,
        });
      },

      clearCart: () => {
        set({ items: [], cartId: null, checkoutUrl: null });
      },

      setCartId: (cartId) => set({ cartId }),
      setCheckoutUrl: (checkoutUrl) => set({ checkoutUrl }),
      setLoading: (isLoading) => set({ isLoading }),

      createCheckout: async (forceNew = false) => {
        const { items, checkoutUrl: existingUrl, setLoading, setCheckoutUrl } = get();
        if (items.length === 0) return null;

        // If we already have a valid checkout URL and not forcing new, reuse it
        if (existingUrl && !forceNew) {
          return existingUrl;
        }

        setLoading(true);
        
        try {
          // Retry logic - try up to 3 times
          let lastError: Error | null = null;
          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              const checkoutUrl = await createStorefrontCheckout(items);
              setCheckoutUrl(checkoutUrl);
              return checkoutUrl;
            } catch (error) {
              console.error(`Failed to create checkout (attempt ${attempt}/3):`, error);
              lastError = error as Error;
              // Wait before retrying (exponential backoff)
              if (attempt < 3) {
                await new Promise(resolve => setTimeout(resolve, 500 * attempt));
              }
            }
          }
          
          console.error('All checkout attempts failed:', lastError);
          return null;
        } finally {
          setLoading(false);
        }
      },

      getTotalItems: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      getTotalPrice: () => {
        return get().items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
      },
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      // Persist only the cart items. Checkout URLs can become stale and must be regenerated.
      partialize: (state) => ({ items: state.items }),
      version: 2,
      migrate: (persistedState) => {
        const state = persistedState as { items?: CartItem[] } | undefined;
        const items = Array.isArray(state?.items) ? dedupeByVariantId(state!.items) : [];
        return { items };
      },
    }
  )
);
